import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  TextField,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  useTheme,
  alpha,
  Stack,
  IconButton,
  Fade,
} from '@mui/material';
import { Email, ArrowBack, CheckCircle } from '@mui/icons-material';
import { api } from '@services';
import axios, { AxiosError } from 'axios';
import { Button } from '@shared/components/ui/Button';
import designSystem from '@shared/utils/designSystem';
import {
  getGlassmorphismBackground,
  getPageBackgroundGradient,
  getBackgroundPattern,
  getBackButtonStyles,
  getTextFieldStyles
} from '../styles/authStyles';

interface ApiErrorResponse {
  error?: string;
  message?: string;
}

function isAxiosErrorWithResponse(error: unknown): error is AxiosError<ApiErrorResponse> {
  return axios.isAxiosError(error) && error.response !== undefined;
}

const ForgotPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState('');

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setLoading(true);
    setError('');

    const email = event.currentTarget.email.value;

    try {
      await api.post('/auth/forgot-password', { email });
      setSubmittedEmail(email);
      setEmailSent(true);
    } catch (error: unknown) {
      if (isAxiosErrorWithResponse(error)) {
        if (error.response?.status === 429) {
          setError('Too many requests. Please wait a few minutes before trying again.');
        } else {
          setError(error.response?.data?.error || 'Failed to send reset email. Please try again.');
        }
      } else if (error instanceof Error && error.message === 'Network Error') {
        setError('Unable to connect to server. Please check your connection.');
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Success state - email sent
  if (emailSent) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          background: getPageBackgroundGradient(theme),
          position: 'relative',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            opacity: theme.palette.mode === 'light' ? 0.4 : 0.3,
            background: getBackgroundPattern(theme),
          }}
        />

        <IconButton
          onClick={() => navigate('/login')}
          sx={getBackButtonStyles(theme)}
        >
          <ArrowBack />
        </IconButton>

        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            zIndex: 1,
          }}
        >
          <Container maxWidth="sm">
            <Fade appear={false} in timeout={800}>
              <Card
                elevation={0}
                sx={{
                  borderRadius: `${designSystem.borderRadius.md}px`,
                  ...getGlassmorphismBackground(theme),
                  boxShadow: designSystem.shadows.xl,
                  overflow: 'hidden',
                  mx: { xs: 2, sm: 4 },
                }}
              >
                <CardContent sx={{ p: { xs: 4, md: 6 }, textAlign: 'center' }}>
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: '50%',
                      backgroundColor: alpha(theme.palette.success.main, 0.15),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mx: 'auto',
                      mb: 3,
                    }}
                  >
                    <CheckCircle sx={{ fontSize: 40, color: theme.palette.success.main }} />
                  </Box>

                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: designSystem.typography.fontWeight.bold,
                      mb: 2,
                      color: theme.palette.text.primary,
                      fontFamily: designSystem.typography.fontFamily.base,
                    }}
                  >
                    Check your email
                  </Typography>

                  <Typography
                    variant="body1"
                    sx={{
                      color: theme.palette.text.secondary,
                      mb: 3,
                      fontFamily: designSystem.typography.fontFamily.base,
                      lineHeight: 1.6,
                    }}
                  >
                    If an account exists for <strong>{submittedEmail}</strong>, you will receive a password reset link shortly.
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{
                      color: theme.palette.text.secondary,
                      mb: 4,
                      fontFamily: designSystem.typography.fontFamily.base,
                    }}
                  >
                    The link will expire in 1 hour.
                  </Typography>

                  <Button
                    fullWidth
                    hierarchy="primary"
                    size="large"
                    onClick={() => navigate('/login')}
                    sx={{
                      py: designSystem.spacing.sm / 8 + 0.75,
                      fontSize: designSystem.typography.fontSize.base,
                      fontFamily: designSystem.typography.fontFamily.base,
                    }}
                  >
                    Return to sign in
                  </Button>
                </CardContent>
              </Card>
            </Fade>
          </Container>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: getPageBackgroundGradient(theme),
        position: 'relative',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          opacity: theme.palette.mode === 'light' ? 0.4 : 0.3,
          background: getBackgroundPattern(theme),
        }}
      />

      <IconButton
        onClick={() => navigate('/login')}
        sx={getBackButtonStyles(theme)}
      >
        <ArrowBack />
      </IconButton>

      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          zIndex: 1,
        }}
      >
        <Container maxWidth="sm">
          <Fade appear={false} in timeout={800}>
            <Card
              elevation={0}
              sx={{
                borderRadius: `${designSystem.borderRadius.md}px`,
                ...getGlassmorphismBackground(theme),
                boxShadow: designSystem.shadows.xl,
                overflow: 'hidden',
                mx: { xs: 2, sm: 4 },
              }}
            >
              <CardContent sx={{ p: { xs: 4, md: 6 } }}>
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: designSystem.typography.fontWeight.bold,
                    mb: designSystem.spacing.sm / 8,
                    color: theme.palette.text.primary,
                    textAlign: 'center',
                    fontSize: { xs: '1.75rem', md: '2.25rem' },
                    fontFamily: designSystem.typography.fontFamily.base,
                    letterSpacing: '-0.02em',
                  }}
                >
                  Reset your password
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    color: theme.palette.text.secondary,
                    mb: designSystem.spacing.xl / 8,
                    textAlign: 'center',
                    fontSize: designSystem.typography.fontSize.base,
                    fontWeight: designSystem.typography.fontWeight.medium,
                    fontFamily: designSystem.typography.fontFamily.base,
                  }}
                >
                  Enter your email address and we&apos;ll send you a link to reset your password.
                </Typography>

                {error && (
                  <Alert
                    severity="error"
                    sx={{
                      mb: designSystem.spacing.lg / 8,
                      borderRadius: `${designSystem.borderRadius.sm}px`,
                      border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
                      backgroundColor: alpha(theme.palette.error.main, 0.15),
                      color: theme.palette.mode === 'light'
                        ? theme.palette.error.dark
                        : theme.palette.error.light,
                      '& .MuiAlert-message': {
                        fontWeight: designSystem.typography.fontWeight.medium,
                        fontFamily: designSystem.typography.fontFamily.base,
                      },
                      '& .MuiAlert-icon': {
                        color: theme.palette.mode === 'light'
                          ? theme.palette.error.dark
                          : theme.palette.error.light,
                      },
                    }}
                  >
                    {error}
                  </Alert>
                )}

                <Box component="form" onSubmit={handleSubmit}>
                  <Stack spacing={3}>
                    <Box>
                      <Typography
                        variant="body2"
                        sx={{
                          mb: designSystem.spacing.xs / 8,
                          color: theme.palette.text.primary,
                          fontWeight: designSystem.typography.fontWeight.semibold,
                          fontSize: designSystem.typography.fontSize.sm,
                          fontFamily: designSystem.typography.fontFamily.base,
                        }}
                      >
                        Email address
                      </Typography>
                      <TextField
                        fullWidth
                        id="email"
                        name="email"
                        type="email"
                        required
                        autoComplete="email"
                        autoFocus
                        disabled={loading}
                        placeholder="you@company.com"
                        InputProps={{
                          startAdornment: (
                            <Email sx={{ color: theme.palette.text.secondary, mr: 1.5, ml: 0.5, fontSize: 20 }} />
                          ),
                        }}
                        sx={getTextFieldStyles(theme)}
                      />
                    </Box>

                    <Box sx={{ pt: 1 }}>
                      <Button
                        type="submit"
                        fullWidth
                        hierarchy="primary"
                        size="large"
                        disabled={loading}
                        sx={{
                          py: designSystem.spacing.sm / 8 + 0.75,
                          fontSize: designSystem.typography.fontSize.base,
                          fontFamily: designSystem.typography.fontFamily.base,
                        }}
                      >
                        {loading ? (
                          <Stack direction="row" spacing={2} alignItems="center">
                            <CircularProgress size={20} color="inherit" />
                            <Typography
                              sx={{
                                fontFamily: designSystem.typography.fontFamily.base,
                                fontWeight: designSystem.typography.fontWeight.semibold,
                              }}
                            >
                              Sending...
                            </Typography>
                          </Stack>
                        ) : (
                          'Send reset link'
                        )}
                      </Button>
                    </Box>
                  </Stack>
                </Box>

                <Box sx={{ textAlign: 'center', mt: designSystem.spacing.xl / 8 }}>
                  <Button
                    hierarchy="tertiary"
                    onClick={() => navigate('/login')}
                    sx={{
                      fontFamily: designSystem.typography.fontFamily.base,
                    }}
                  >
                    Back to sign in
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Fade>
        </Container>
      </Box>
    </Box>
  );
};

export default ForgotPasswordPage;
